/**
 * animations.js — Auto Adrenaline
 * Secondary animation layer loaded after main.js.
 * Handles: stats counters, parallax, FAQ accordion, timeline activation,
 * magnetic buttons, split-text char stagger, video lazy-load, RGB border hover.
 */
(function () {
  'use strict';

  /* ─── Helpers ──────────────────────────────────────────────────────────── */

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function easeOutExpo(t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); }

  function observe(elements, callback, options) {
    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (el) { callback(el, true); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          callback(entry.target, true);
          io.unobserve(entry.target);
        }
      });
    }, Object.assign({ threshold: 0.25 }, options));
    elements.forEach(function (el) { io.observe(el); });
  }

  /* ─── 1. Animated stat counters ────────────────────────────────────────── */
  // Targets: [data-count-to="9500"] [data-count-suffix="+"]
  // Works on .stats__number elements inside .stats section.

  function initStatCounters() {
    var counters = qsa('[data-count-to]');
    if (!counters.length) return;

    observe(counters, function (el) {
      var target = parseInt(el.getAttribute('data-count-to'), 10);
      var suffix = el.getAttribute('data-count-suffix') || '';
      var duration = 1800;

      if (prefersReducedMotion) {
        el.textContent = target.toLocaleString() + suffix;
        return;
      }

      var start = null;
      function step(ts) {
        if (!start) start = ts;
        var elapsed = ts - start;
        var progress = Math.min(elapsed / duration, 1);
        var value = Math.floor(easeOutExpo(progress) * target);
        el.textContent = value.toLocaleString() + suffix;
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }

  /* ─── 2. Parallax backgrounds ──────────────────────────────────────────── */
  // Targets: [data-parallax] — moves background-position-y on scroll.
  // Skips if prefers-reduced-motion.

  function initParallax() {
    if (prefersReducedMotion) return;

    var elements = qsa('[data-parallax]');
    if (!elements.length) return;

    var ticking = false;

    function update() {
      var scrollY = window.scrollY;
      elements.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.3;
        var rect = el.getBoundingClientRect();
        var offset = (rect.top + scrollY) * speed;
        el.style.backgroundPositionY = -offset + 'px';
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* ─── 3. FAQ accordion ─────────────────────────────────────────────────── */
  // Targets: .faq__item with .faq__question button and .faq__answer panel.
  // Accessible: aria-expanded, aria-controls, aria-hidden, role="region".

  function initFaqAccordion() {
    var items = qsa('.faq__item');
    if (!items.length) return;

    items.forEach(function (item) {
      var btn = qs('.faq__question', item);
      var answer = qs('.faq__answer', item);
      if (!btn || !answer) return;

      btn.addEventListener('click', function () {
        var isOpen = item.classList.contains('faq__item--open');

        // Close all
        items.forEach(function (other) {
          other.classList.remove('faq__item--open');
          var otherBtn = qs('.faq__question', other);
          var otherAns = qs('.faq__answer', other);
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
          if (otherAns) {
            otherAns.style.maxHeight = '0';
            otherAns.style.opacity = '0';
          }
        });

        // Open clicked (if was closed)
        if (!isOpen) {
          item.classList.add('faq__item--open');
          btn.setAttribute('aria-expanded', 'true');
          answer.style.maxHeight = answer.scrollHeight + 'px';
          answer.style.opacity = '1';
        }
      });
    });
  }

  /* ─── 4. Timeline milestone activation ─────────────────────────────────── */
  // Targets: .timeline__item — adds .timeline__item--active when scrolled into view.

  function initTimeline() {
    var items = qsa('.timeline__item');
    if (!items.length) return;

    observe(items, function (el) {
      el.classList.add('timeline__item--active');
    }, { threshold: 0.4 });
  }

  /* ─── 5. Magnetic buttons ───────────────────────────────────────────────── */
  // Targets: [data-magnetic] — nudges the element toward the cursor on hover.
  // Strength controlled by data-magnetic="0.3" (0 = rigid, 1 = very bendy).

  function initMagneticButtons() {
    if (prefersReducedMotion) return;

    var elements = qsa('[data-magnetic]');
    elements.forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-magnetic')) || 0.3;

      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = (e.clientX - cx) * strength;
        var dy = (e.clientY - cy) * strength;
        el.style.transform = 'translate(' + dx + 'px, ' + dy + 'px)';
      });

      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
        el.style.transition = 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)';
        setTimeout(function () { el.style.transition = ''; }, 500);
      });
    });
  }

  /* ─── 6. Split-text char stagger ───────────────────────────────────────── */
  // Targets: [data-split-text] — wraps each char in a span and staggers them in.
  // Respects .reveal observer — fires when the element enters view.

  function initSplitText() {
    if (prefersReducedMotion) return;

    var elements = qsa('[data-split-text]');
    elements.forEach(function (el) {
      var text = el.textContent;
      el.innerHTML = '';
      el.setAttribute('aria-label', text); // keep accessible
      text.split('').forEach(function (char, i) {
        var outer = document.createElement('span');
        outer.className = 'split-char';
        outer.setAttribute('aria-hidden', 'true');
        outer.style.display = 'inline-block';
        outer.style.overflow = 'hidden';

        var inner = document.createElement('span');
        inner.className = 'split-char__inner';
        inner.style.display = 'inline-block';
        inner.style.transform = 'translateY(110%)';
        inner.style.transition = 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ' + (i * 0.04) + 's';
        inner.textContent = char === ' ' ? ' ' : char;

        outer.appendChild(inner);
        el.appendChild(outer);
      });

      observe([el], function () {
        qsa('.split-char__inner', el).forEach(function (span) {
          span.style.transform = 'translateY(0)';
        });
      }, { threshold: 0.1 });
    });
  }

  /* ─── 7. Lazy video load ────────────────────────────────────────────────── */
  // Targets: video[data-src] — sets src when video is near viewport.

  function initLazyVideo() {
    var videos = qsa('video[data-src]');
    if (!videos.length) return;

    observe(videos, function (el) {
      el.src = el.getAttribute('data-src');
      el.removeAttribute('data-src');
      el.load();
    }, { rootMargin: '200px' });
  }

  /* ─── 8. RGB border on hover (card glow) ───────────────────────────────── */
  // Targets: .category-card — updates --mouse-x / --mouse-y CSS vars for spotlight.

  function initSpotlightCards() {
    if (prefersReducedMotion) return;

    var cards = qsa('.category-card');
    cards.forEach(function (card) {
      card.addEventListener('mousemove', function (e) {
        var rect = card.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        card.style.setProperty('--mouse-x', x + '%');
        card.style.setProperty('--mouse-y', y + '%');
      });
    });
  }

  /* ─── 9. Product image zoom follow cursor ───────────────────────────────── */
  // Targets: .product-gallery__main img — transform-origin follows cursor.

  function initGalleryZoom() {
    var mainImg = qs('.product-gallery__main img');
    if (!mainImg) return;

    mainImg.parentElement.addEventListener('mousemove', function (e) {
      var rect = mainImg.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      mainImg.style.transformOrigin = x + '% ' + y + '%';
    });
  }

  /* ─── 10. Scroll-linked progress bar ───────────────────────────────────── */
  // Renders a thin red bar at the top tracking page read progress.
  // Only on article/blog templates.

  function initReadProgress() {
    if (!document.body.classList.contains('template-article')) return;

    var bar = document.createElement('div');
    bar.id = 'read-progress';
    bar.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'height:2px',
      'width:0%', 'background:var(--red)', 'z-index:9999',
      'transition:width 0.1s linear', 'pointer-events:none'
    ].join(';');
    document.body.appendChild(bar);

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var docH = document.documentElement.scrollHeight - window.innerHeight;
          var pct = docH > 0 ? (window.scrollY / docH) * 100 : 0;
          bar.style.width = Math.min(pct, 100) + '%';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ─── 11. Hover sound (opt-in) ─────────────────────────────────────────── */
  // Targets: [data-hover-sound] — plays a short tick on mouseenter.
  // Requires user gesture (click) before audio context unlocks.

  function initHoverSound() {
    var elements = qsa('[data-hover-sound]');
    if (!elements.length) return;

    var ctx = null;
    var unlocked = false;

    document.addEventListener('click', function () {
      if (unlocked) return;
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      unlocked = true;
    }, { once: false });

    elements.forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        if (!ctx || !unlocked) return;
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(1800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.05);
        gain.gain.setValueAtTime(0.04, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.06);
      });
    });
  }

  /* ─── 12. Countdown digit flip animation ───────────────────────────────── */
  // Targets: .countdown__digit — adds .flip class on value change for CSS animation.
  // Pairs with the CSS @keyframes countdown-flip in style.css.

  function initCountdownFlip() {
    var digits = qsa('.countdown__digit');
    if (!digits.length) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'characterData' || m.type === 'childList') {
          var el = m.target.nodeType === 3 ? m.target.parentElement : m.target;
          el.classList.remove('flip');
          void el.offsetWidth; // reflow to restart animation
          el.classList.add('flip');
        }
      });
    });

    digits.forEach(function (d) {
      observer.observe(d, { characterData: true, childList: true, subtree: true });
    });
  }

  /* ─── 13. Scroll-snap product gallery ──────────────────────────────────── */
  // Touch swipe on mobile gallery — updates active thumbnail highlight.

  function initMobileGallerySwipe() {
    var track = qs('.product-gallery__main');
    if (!track) return;

    var thumbs = qsa('.product-gallery__thumb');

    var startX = 0;
    track.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
    }, { passive: true });

    track.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 30) return;
      var active = qs('.product-gallery__thumb--active');
      if (!active) return;
      var idx = thumbs.indexOf(active);
      var next = dx < 0 ? idx + 1 : idx - 1;
      if (next >= 0 && next < thumbs.length) {
        thumbs[next].click();
      }
    }, { passive: true });
  }

  /* ─── Init ─────────────────────────────────────────────────────────────── */

  function init() {
    initStatCounters();
    initParallax();
    initFaqAccordion();
    initTimeline();
    initMagneticButtons();
    initSplitText();
    initLazyVideo();
    initSpotlightCards();
    initGalleryZoom();
    initReadProgress();
    initHoverSound();
    initCountdownFlip();
    initMobileGallerySwipe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for deferred calls (e.g., after section reload via Theme Editor)
  window.AutoAdrenaline = window.AutoAdrenaline || {};
  window.AutoAdrenaline.animations = {
    initFaqAccordion: initFaqAccordion,
    initStatCounters: initStatCounters,
    initTimeline: initTimeline,
    initMagneticButtons: initMagneticButtons,
    initSplitText: initSplitText
  };

})();
